import React from 'react';
import Pagination, {
    PagingData,
    PageSizeOption,
} from '../Pagination/Pagination';

export interface ColumnConfig<T> {
    key: keyof T | string;
    header: string;
    render?: (value: any, row: T) => React.ReactNode;
    clickable?: boolean;
    width?: string;
}

// 헤더 버튼 인터페이스
export interface HeaderButton {
    label: string;
    icon?: string;
    className?: string;
    onClick: () => void;
}

// 액션 버튼 인터페이스 (새로 추가)
export interface ActionButton<T> {
    label: string;
    icon?: string;
    className: string;
    onClick: (row: T) => void;
}

// 기본 페이지 크기 옵션
const DEFAULT_PAGE_SIZE_OPTIONS: PageSizeOption[] = [
    { value: 10, label: '10개씩보기' },
    { value: 20, label: '20개씩보기' },
    { value: 30, label: '30개씩보기' },
    { value: 50, label: '50개씩보기' },
    { value: 100, label: '100개씩보기' },
    { value: 500, label: '500개씩보기' },
];

interface DataTableProps<T> {
    data?: T[];
    columns: ColumnConfig<T>[];
    paging?: PagingData;
    pageSizeOptions?: PageSizeOption[];
    headerButtons?: HeaderButton[];
    actionButtons?: ActionButton<T>[]; // 액션 버튼 추가
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    onClickRow?: (row: T) => void;
    showHeader?: boolean;
    cardClassName?: string;
    noneFooterPadding?: boolean;
}

const DataTable = <T,>({
    data,
    columns,
    paging,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    headerButtons = [],
    actionButtons = [], // 새로 추가된 props
    onPageChange,
    onPageSizeChange,
    onClickRow,
    showHeader = true,
    cardClassName = '',
    noneFooterPadding = false,
}: DataTableProps<T>) => {
    // 액션 버튼이 있으면 자동으로 액션 컬럼 추가
    const finalColumns = React.useMemo((): ColumnConfig<T>[] => {
        if (actionButtons.length > 0) {
            const actionColumn: ColumnConfig<T> = {
                key: 'actions',
                header: '액션',
                clickable: false,
                render: (_value: any, row: T) => (
                    <div className="d-flex gap-1 justify-content-center">
                        {actionButtons.map((button, index) => (
                            <button
                                key={index}
                                type="button"
                                className={button.className}
                                onClick={e => {
                                    e.stopPropagation(); // row 클릭 이벤트 방지
                                    button.onClick(row);
                                }}
                                title={button.label}
                            >
                                {button.icon && <i className={button.icon}></i>}
                                {!button.icon && button.label}
                            </button>
                        ))}
                    </div>
                ),
            };
            return [...columns, actionColumn];
        }
        return columns;
    }, [columns, actionButtons]);

    // 셀 값 가져오기
    const getCellValue = (row: T, key: keyof T | string): any => {
        if (typeof key === 'string' && key.includes('.')) {
            return key.split('.').reduce((obj: any, k) => obj?.[k], row);
        }
        return (row as any)[key];
    };

    return (
        <div className={`card ${cardClassName} custom-card`}>
            {/* 헤더 */}
            {showHeader && (
                <div className="card-header justify-content-between">
                    <div className="d-flex gap-2">
                        {headerButtons.map((button, index) => (
                            <a
                                key={index}
                                className={
                                    button.className ||
                                    'btn btn-sm btn-primary btn-wave'
                                }
                                onClick={button.onClick}
                            >
                                {button.icon && (
                                    <i
                                        className={`${button.icon} fw-medium me-1`}
                                    ></i>
                                )}
                                {button.label}
                            </a>
                        ))}
                    </div>
                    {paging && (
                        <div className="d-flex align-items-center gap-3">
                            <div>Total : {paging.totalRecord}</div>
                            <select
                                className="form-select form-select-sm cursor-pointer w-auto"
                                value={paging.pageSize}
                                onChange={e =>
                                    onPageSizeChange?.(Number(e.target.value))
                                }
                            >
                                {pageSizeOptions.map(option => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            )}

            {/* 테이블 */}
            <div className="card-body p-0">
                <div className="table-responsive">
                    <table className="table text-nowrap table-striped table-hover">
                        <thead>
                            <tr className="text-center">
                                {finalColumns.map((column, index) => (
                                    <th
                                        key={index}
                                        scope="col"
                                        style={
                                            column.width
                                                ? { width: column.width }
                                                : undefined
                                        }
                                    >
                                        {column.header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data?.length === 0 || data === undefined ? (
                                <tr>
                                    <td
                                        colSpan={finalColumns.length}
                                        className="text-center py-4"
                                    >
                                        <span className="text-gray fs-6">
                                            <div className="fs-2">😳 ️</div>
                                            일치하는 데이터가 없습니다.
                                        </span>
                                    </td>
                                </tr>
                            ) : (
                                data?.map((row, rowIndex) => (
                                    <tr key={rowIndex} className="text-center">
                                        {finalColumns.map(
                                            (column, colIndex) => {
                                                const cellValue = getCellValue(
                                                    row,
                                                    column.key,
                                                );
                                                return (
                                                    <td
                                                        key={colIndex}
                                                        className={
                                                            column.clickable
                                                                ? 'clickable'
                                                                : ''
                                                        }
                                                        onClick={
                                                            column.clickable
                                                                ? onClickRow
                                                                    ? () =>
                                                                          onClickRow(
                                                                              row,
                                                                          )
                                                                    : undefined
                                                                : undefined
                                                        }
                                                    >
                                                        {column.render
                                                            ? column.render(
                                                                  cellValue,
                                                                  row,
                                                              )
                                                            : cellValue}
                                                    </td>
                                                );
                                            },
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* 페이징 */}
            {paging && paging.totalPage > 1 ? (
                <Pagination paging={paging} onPageChange={onPageChange} />
            ) : (
                <div className={noneFooterPadding ? '' : 'pt-5'}></div>
            )}
        </div>
    );
};

export default DataTable;
